import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { Layout } from '../components/layout';
import { useAuth } from '../context/AuthContext';
import usePermissions from '../hooks/usePermissions';
import api, { getAssetUrl } from '../utils/api';
import { toast } from 'react-toastify';
import { 
  Shield, ExternalLink, MapPin, ThumbsUp, ThumbsDown, User, MessageCircle, CheckCircle, 
  Clock, ChevronDown, Flag, Cloud, Droplets, Wind, Thermometer, Sun, 
  CloudRain, CloudSnow, CloudLightning, CloudFog, Trophy, Medal, Award, Star
} from 'lucide-react';
import IssueVerificationModal from '../components/IssueVerificationModal';
import { FramedAvatar, StyledUsername } from '../components/cosmetics';
import ShareButton from '../components/ShareButton';
import HeroMap from '../components/HeroMap';

// Weather code mappings
const WMO_ICONS = {
  0: Sun, 1: Sun, 2: Cloud, 3: Cloud,
  45: CloudFog, 48: CloudFog,
  51: Droplets, 53: Droplets, 55: Droplets,
  61: CloudRain, 63: CloudRain, 65: CloudRain,
  71: CloudSnow, 73: CloudSnow, 75: CloudSnow, 77: CloudSnow,
  80: CloudRain, 81: CloudRain, 82: CloudRain,
  85: CloudSnow, 86: CloudSnow,
  95: CloudLightning, 96: CloudLightning, 99: CloudLightning,
};

const WMO_LABELS = {
  0: 'Clear', 1: 'Mostly Clear', 2: 'Partly Cloudy', 3: 'Overcast',
  45: 'Foggy', 48: 'Rime Fog',
  51: 'Light Drizzle', 53: 'Drizzle', 55: 'Heavy Drizzle',
  61: 'Light Rain', 63: 'Rain', 65: 'Heavy Rain',
  71: 'Light Snow', 73: 'Snow', 75: 'Heavy Snow', 77: 'Snow Grains',
  80: 'Light Showers', 81: 'Showers', 82: 'Heavy Showers',
  85: 'Snow Showers', 86: 'Heavy Snow Showers',
  95: 'Thunderstorm', 96: 'Hail Storm', 99: 'Heavy Hail',
};

const Home = () => {
  const [posts, setPosts] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [likedPosts, setLikedPosts] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  const [expandedPost, setExpandedPost] = useState(null);
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [commentVotes, setCommentVotes] = useState({}); // Track user's votes on comments
  const [verifyingIssue, setVerifyingIssue] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const postsPerPage = 30;
  const { user } = useAuth();
  const { isAdmin: isAdminUser, isModerator } = usePermissions();
  
  // Widget data
  const [weather, setWeather] = useState(null);
  const [leaders, setLeaders] = useState([]);
  const [latestBadge, setLatestBadge] = useState(null);

  // Hero map crossfade — show landing image first, transition to map after 5s
  const [showMap, setShowMap] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShowMap(true), 5000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    fetchData();
    fetchWeather();
    fetchWidgetData();
  }, [currentPage]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * postsPerPage;
      const [postsRes, adsRes] = await Promise.all([
        api.get(`/posts?skip=${skip}&limit=${postsPerPage}`),
        api.get('/ads'),
      ]);
      setPosts(postsRes.data.posts);
      setTotalPosts(postsRes.data.total || 0);
      setAds(adsRes.data);
      
      // Auto-fetch comments for posts that have them
      const postsWithComments = postsRes.data.posts.filter(p => p.comment_count > 0);
      if (postsWithComments.length > 0) {
        const commentsMap = {};
        await Promise.all(
          postsWithComments.slice(0, 10).map(async (post) => { // Limit to first 10 to avoid too many requests
            try {
              const res = await api.get(`/posts/${post.id}/comments`);
              commentsMap[post.id] = res.data;
            } catch (err) {
              console.error(`Failed to fetch comments for post ${post.id}`);
            }
          })
        );
        setComments(commentsMap);
      }
      
      if (user && postsRes.data.posts.length > 0) {
        try {
          const postIds = postsRes.data.posts.map(p => p.id);
          const res = await api.post('/posts/bulk-like-status', postIds);
          setLikedPosts(res.data.liked || {});
        } catch (error) {
          console.error('Error checking likes:', error);
          setLikedPosts({});
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const canModeratePost = (post) => {
    if (isAdminUser) return true;
    if (!isModerator || !user?.ward) return false;
    const postWard = post.user_ward || post.ward;
    return postWard === user.ward;
  };

  const handleModeratePost = async (postId) => {
    try {
      if (isAdminUser) {
        await api.put(`/admin/posts/${postId}/moderate?action=reject`);
      } else {
        await api.put(`/community-leader/posts/${postId}/moderate?status=moderated`);
      }
      toast.success('Post hidden');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to moderate post');
    }
  };

  const fetchWeather = async () => {
    try {
      const res = await fetch(
        'https://api.open-meteo.com/v1/forecast?latitude=50.45&longitude=-104.62&current=temperature_2m,relative_humidity_2m,wind_speed_10m,weather_code&temperature_unit=celsius&wind_speed_unit=kmh&timezone=America/Regina'
      );
      const data = await res.json();
      if (data.current) {
        setWeather({
          temp: Math.round(data.current.temperature_2m),
          humidity: data.current.relative_humidity_2m,
          wind: Math.round(data.current.wind_speed_10m),
          code: data.current.weather_code,
        });
      }
    } catch (err) {
      console.error('Weather fetch failed:', err);
    }
  };

  const fetchWidgetData = async () => {
    try {
      const [leadersRes, badgeRes] = await Promise.all([
        api.get('/leaderboard?limit=5'),
        api.get('/badges/latest')
      ]);
      setLeaders(leadersRes.data.leaderboard || []);
      setLatestBadge(badgeRes.data.badge);
    } catch (err) {
      console.error('Widget data fetch failed:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleLike = async (postId) => {
    if (!user) {
      toast.error('Please log in to like posts');
      return;
    }

    try {
      if (likedPosts[postId]) {
        await api.delete(`/posts/${postId}/like`);
        setLikedPosts({ ...likedPosts, [postId]: false });
        setPosts(posts.map(p => 
          p.id === postId ? { ...p, likes: Math.max(0, (p.likes || 1) - 1) } : p
        ));
      } else {
        await api.post(`/posts/${postId}/like`);
        setLikedPosts({ ...likedPosts, [postId]: true });
        setPosts(posts.map(p => 
          p.id === postId ? { ...p, likes: (p.likes || 0) + 1 } : p
        ));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update like');
    }
  };

  const toggleComments = async (postId) => {
    if (expandedComments[postId]) {
      setExpandedComments({ ...expandedComments, [postId]: false });
    } else {
      try {
        const res = await api.get(`/posts/${postId}/comments`);
        setComments({ ...comments, [postId]: res.data });
        setExpandedComments({ ...expandedComments, [postId]: true });
      } catch (error) {
        toast.error('Failed to load comments');
      }
    }
  };

  const handleAddComment = async (postId) => {
    if (!user) {
      toast.error('Please log in to comment');
      return;
    }

    const content = newComment[postId]?.trim();
    if (!content) {
      toast.error('Comment cannot be empty');
      return;
    }

    try {
      const res = await api.post(`/posts/${postId}/comments`, { content });
      setComments({
        ...comments,
        [postId]: [...(comments[postId] || []), res.data]
      });
      setNewComment({ ...newComment, [postId]: '' });
      setPosts(posts.map(p => 
        p.id === postId ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p
      ));
      toast.success('Comment added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add comment');
    }
  };

  // Handle comment voting (like/dislike)
  const handleCommentVote = async (commentId, voteType) => {
    if (!user) {
      toast.error('Please log in to vote');
      return;
    }
    
    const currentVote = commentVotes[commentId];
    const newVote = currentVote === voteType ? null : voteType; // Toggle if same vote
    
    try {
      await api.post(`/comments/${commentId}/vote`, { vote_type: newVote });
      setCommentVotes({ ...commentVotes, [commentId]: newVote });
      
      // Update comment vote counts locally
      Object.keys(comments).forEach(postId => {
        setComments(prev => ({
          ...prev,
          [postId]: (prev[postId] || []).map(c => {
            if (c.id === commentId) {
              let upvotes = c.upvotes || 0;
              let downvotes = c.downvotes || 0;
              
              // Remove old vote
              if (currentVote === 'up') upvotes--;
              if (currentVote === 'down') downvotes--;
              
              // Add new vote
              if (newVote === 'up') upvotes++;
              if (newVote === 'down') downvotes++;
              
              return { ...c, upvotes, downvotes };
            }
            return c;
          })
        }));
      });
    } catch (error) {
      // Silently fail - voting may not be implemented yet
      console.error('Vote failed:', error);
    }
  };

  const mainSponsor = ads.find(ad => ad.ad_type === 'main_sponsor') || null;
  const feedAds = ads.filter(ad => ad.ad_type === 'standard' || (!ad.ad_type && ad !== mainSponsor));
  
  const WeatherIcon = weather ? (WMO_ICONS[weather.code] || Cloud) : Cloud;
  const weatherLabel = weather ? (WMO_LABELS[weather.code] || 'Unknown') : '';

  const getRankIcon = (i) => {
    if (i === 0) return <Trophy className="w-4 h-4 text-yellow-500" />;
    if (i === 1) return <Medal className="w-4 h-4 text-gray-400" />;
    if (i === 2) return <Award className="w-4 h-4 text-amber-700" />;
    return <span className="text-zinc-500 font-mono text-xs">#{i + 1}</span>;
  };

  return (
    <Layout showFooter={true}>
      {/* HERO SECTION — landing image crossfades to live Regina map after 5s */}
      <section
        className="relative overflow-hidden"
        style={{ minHeight: '65vh' }}
        data-testid="hero-section"
      >
        {/* Layer 1: background photo — fades out when map takes over */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1665587195322-811a06b7aa50?w=1920&q=80')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: showMap ? 0 : 1,
            transition: 'opacity 1.8s ease-in-out',
          }}
        />

        {/* Layer 2: live Mapbox map — fades in after 5s */}
        <HeroMap visible={showMap} />

        {/* Permanent dark overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: showMap
              ? 'linear-gradient(to bottom, rgba(0,0,0,0.45), rgba(10,10,10,0.65))'
              : 'linear-gradient(to bottom, rgba(0,0,0,0.78), rgba(10,10,10,0.60))',
            transition: 'background 1.8s ease-in-out',
          }}
        />

        {/* Map legend — bottom left, only when map is shown */}
        <div
          className="absolute bottom-5 left-6 flex flex-col gap-1.5 z-10"
          style={{ opacity: showMap ? 1 : 0, transition: 'opacity 1.2s ease 1.5s', pointerEvents: 'none' }}
        >
          {[
            { color: '#ef4444', label: 'Open Issue' },
            { color: '#fb923c', label: 'Verified Issue' },
            { color: '#4ade80', label: 'Resolved' },
            { color: '#fbbf24', label: 'Badge Earned' },
            { color: '#94a3b8', label: 'Check-in' },
          ].map(({ color, label }) => (
            <div key={label} className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
              <span className="font-mono text-[9px] text-white/50 uppercase tracking-widest">{label}</span>
            </div>
          ))}
        </div>

        {/* Content — absolutely positioned over all layers, truly centred */}
        <div className="absolute inset-0 flex items-center justify-center z-10 px-6">
          <div className="text-center w-full max-w-3xl">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-[#22C55E] font-mono text-xs tracking-[0.3em] uppercase mb-4"
            >
              The Real Regina Experience
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-white mb-2"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              SHOW US YOUR
            </motion.h1>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase mb-6"
              style={{ fontFamily: 'Chivo, sans-serif', color: '#FACC15' }}
            >
              REGINA.
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/submit"
                className="inline-flex items-center justify-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] text-black font-bold py-3 px-6 tracking-wider transition-all"
                data-testid="report-issue-btn"
              >
                REPORT AN ISSUE
              </Link>
              <button
                onClick={async () => {
                  if (!user) { window.location.href = '/register'; return; }
                  if (!navigator.geolocation) { toast.error('Geolocation not supported on this device'); return; }
                  toast.info('Getting your location…', { autoClose: 2000 });
                  navigator.geolocation.getCurrentPosition(
                    async (pos) => {
                      try {
                        const res = await api.post('/badges/checkin', {
                          latitude: pos.coords.latitude,
                          longitude: pos.coords.longitude,
                          accuracy: pos.coords.accuracy
                        });
                        const d = res.data;
                        if (d.badges_earned?.length > 0) {
                          toast.success(`You earned: ${d.badges_earned.map(b => b.name).join(', ')}!`, { autoClose: 5000 });
                        } else {
                          toast.success("You're on the map! No badge hotspots nearby right now — keep exploring.", { autoClose: 4000 });
                        }
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || 'Check-in failed');
                      }
                    },
                    () => toast.error('Location access denied — enable it to check in')
                  );
                }}
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white/20 hover:border-[#FACC15] hover:text-[#FACC15] text-white font-bold py-3 px-6 tracking-wider transition-all cursor-pointer"
                data-testid="checkin-btn"
              >
                CHECK IN TO WIN
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* LIVE TICKER */}
      <div className="bg-[#0A0A0A] border-y border-zinc-800 py-3">
        <Marquee speed={40} gradient={false} className="text-zinc-400">
          <span className="mx-8 font-mono text-sm">
            <span className="text-[#22C55E]">●</span> LIVE FEED: {totalPosts} ACTIVE REPORTS
          </span>
          <span className="mx-8 font-mono text-sm">
            <span className="text-[#FACC15]">●</span> REGINA WEATHER: {weather ? `${weather.temp}°C ${weatherLabel}` : 'Loading...'}
          </span>
          {latestBadge && (
            <span className="mx-8 font-mono text-sm">
              <span className="text-[#22C55E]">●</span> NEW BADGE: {latestBadge.name}
            </span>
          )}
          {leaders[0] && (
            <span className="mx-8 font-mono text-sm">
              <span className="text-[#FACC15]">●</span> TOP CONTRIBUTOR: {leaders[0].name} ({leaders[0].total_points} pts)
            </span>
          )}
        </Marquee>
      </div>

      {/* MAIN CONTENT */}
      <div className="bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          
          {/* SITE SPONSOR */}
          {mainSponsor && (
            <div className="mb-8 rounded overflow-hidden border border-[#C5A52A]/30 bg-gradient-to-r from-[#1a1a1a] to-[#111]" data-testid="main-sponsor">
              {mainSponsor.image_url ? (
                <a 
                  href={mainSponsor.link || '#'} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block"
                >
                  <img 
                    src={mainSponsor.image_url} 
                    alt={mainSponsor.title}
                    className="w-full h-auto max-h-[200px] object-cover"
                  />
                </a>
              ) : (
                <div className="p-6 text-center">
                  <p className="font-mono text-[10px] text-[#C5A52A]/50 mb-2 tracking-widest">SITE SPONSOR</p>
                  <h2 className="font-headline text-2xl md:text-3xl text-white mb-1">
                    {mainSponsor.title}
                  </h2>
                  <p className="font-mono text-xs text-gray-400">
                    {mainSponsor.content}
                  </p>
                  {mainSponsor.link && (
                    <a
                      href={mainSponsor.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-3 text-[#C5A52A] hover:text-[#D4B93A] font-display text-xs tracking-wider transition-colors"
                    >
                      VISIT SPONSOR <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              )}
              <div className="bg-black/50 px-4 py-2 flex items-center justify-center gap-2">
                <Shield className="w-3 h-3 text-[#C5A52A]" />
                <span className="font-mono text-[10px] text-[#C5A52A]/70 tracking-widest">
                  OFFICIAL SITE SPONSOR
                </span>
              </div>
            </div>
          )}

          {/* WIDGETS ROW */}
          <div className="flex gap-3 mb-8 overflow-x-auto pb-2 sm:overflow-x-visible sm:grid sm:grid-cols-3 sm:gap-4 sm:pb-0">
            {/* Weather Widget */}
            <div className="flex-shrink-0 w-[72vw] sm:w-auto bg-[#111] border border-zinc-800 rounded p-4" data-testid="weather-widget">
              <div className="flex items-center gap-2 mb-2">
                <Thermometer className="w-4 h-4 text-[#C5A52A]" />
                <span className="font-mono text-xs text-[#C5A52A] tracking-wider">REGINA WEATHER</span>
              </div>
              {weather ? (
                <div className="flex items-center gap-3">
                  <WeatherIcon className="w-8 h-8 text-[#C5A52A]" />
                  <div>
                    <div className="text-2xl font-bold text-white">{weather.temp}°C</div>
                    <div className="font-mono text-xs text-zinc-500">{weatherLabel}</div>
                  </div>
                </div>
              ) : (
                <div className="text-zinc-500 text-sm">Loading...</div>
              )}
            </div>

            {/* Latest Badge Widget */}
            <div className="flex-shrink-0 w-[72vw] sm:w-auto bg-[#111] border border-zinc-800 rounded p-4" data-testid="latest-badge-widget">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-4 h-4 text-[#C5A52A]" />
                <span className="font-mono text-xs text-[#C5A52A] tracking-wider">LATEST BADGE</span>
              </div>
              {latestBadge ? (
                <Link to="/badges" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                  {latestBadge.icon_url ? (
                    <img src={latestBadge.icon_url} alt={latestBadge.name} className="w-10 h-10 rounded" />
                  ) : (
                    <div className="w-10 h-10 bg-[#C5A52A]/20 rounded flex items-center justify-center">
                      <Award className="w-5 h-5 text-[#C5A52A]" />
                    </div>
                  )}
                  <div>
                    <div className="text-white text-sm font-bold truncate">{latestBadge.name}</div>
                    <div className="text-[#22C55E] font-mono text-xs">+{latestBadge.reward_points || 0} pts</div>
                  </div>
                </Link>
              ) : (
                <div className="text-zinc-500 text-sm">No badges yet</div>
              )}
            </div>

            {/* Top Leaderboard Widget */}
            <div className="flex-shrink-0 w-[72vw] sm:w-auto bg-[#111] border border-zinc-800 rounded p-4" data-testid="leaderboard-widget">
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="w-4 h-4 text-[#C5A52A]" />
                <span className="font-mono text-xs text-[#C5A52A] tracking-wider">TOP CONTRIBUTORS</span>
              </div>
              <div className="space-y-1">
                {leaders.slice(0, 3).map((leader, i) => (
                  <Link key={leader.user_id} to={`/profile/${leader.user_id}`} className="flex items-center gap-2 hover:bg-zinc-800/50 rounded p-1 transition-colors">
                    {getRankIcon(i)}
                    <span className="text-white text-sm truncate flex-1">{leader.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* POSTS FEED - Single Column */}
          <div data-testid="posts-feed">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C5A52A] mx-auto"></div>
                <p className="text-gray-500 mt-4 font-mono text-sm">Loading records...</p>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12 bg-[#111] border border-zinc-800 rounded">
                <p className="text-gray-500 font-mono">No records yet. Be the first to submit!</p>
                {user ? (
                  <Link
                    to="/submit"
                    className="inline-block mt-4 bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-display text-sm py-2 px-6 tracking-wider transition-colors"
                  >
                    SUBMIT A RECORD
                  </Link>
                ) : (
                  <Link
                    to="/register"
                    className="inline-block mt-4 bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-display text-sm py-2 px-6 tracking-wider transition-colors"
                  >
                    REGISTER TO SUBMIT
                  </Link>
                )}
              </div>
            ) : (
              <div>
                {posts.map((post, index) => (
                  <div key={post.id}>
                    {/* Post Card */}
                    <article 
                      className={`mb-6 border rounded transition-all relative ${
                        post.is_featured 
                          ? 'bg-[#2a2410] border-[#C5A52A] shadow-[0_0_30px_rgba(197,165,42,0.4)]' 
                          : 'bg-[#111] border-zinc-800'
                      }`}
                      data-testid={`post-card-${post.id}`}
                    >
                      {/* Featured Badge */}
                      {post.is_featured && (
                        <div className="absolute -top-3 left-6 bg-[#C5A52A] text-black px-3 py-1 font-display text-xs tracking-wider z-10">
                          FEATURED
                        </div>
                      )}
                      
                      <div className="p-6">
                        {/* Dropdown Menu */}
                        <div className="absolute top-4 right-4 z-20 flex items-center gap-1">
                          {user && post.category === 'issue' && post.verification_status !== 'fixed' && post.user_id !== user?.id && (
                            <button
                              onClick={() => setVerifyingIssue(post)}
                              className="bg-zinc-800 hover:bg-[#C5A52A]/20 border border-[#C5A52A]/40 rounded p-1.5 transition-colors"
                              title="Verify this issue"
                              data-testid={`verify-issue-btn-${post.id}`}
                            >
                              <Shield className="w-4 h-4 text-[#C5A52A]" />
                            </button>
                          )}
                          <button
                            onClick={() => setExpandedPost(expandedPost === post.id ? null : post.id)}
                            className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded p-1.5 transition-colors"
                          >
                            <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${expandedPost === post.id ? 'rotate-180' : ''}`} />
                          </button>
                          
                          {expandedPost === post.id && (
                            <>
                              <div className="fixed inset-0" onClick={() => setExpandedPost(null)} />
                              <div className="absolute top-full right-0 mt-2 bg-[#0a0a0a] border border-zinc-800 rounded shadow-2xl min-w-[220px] z-50">
                                <div className="p-3 border-b border-zinc-800">
                                  <div className="text-gray-500 text-xs mb-1 font-mono">POST NUMBER</div>
                                  <div className="text-white text-sm font-mono">#{post.id.slice(0, 8).toUpperCase()}</div>
                                </div>
                                <div className="p-3 border-b border-zinc-800">
                                  <div className="text-gray-500 text-xs mb-1 font-mono">OUTSTANDING</div>
                                  <div className="text-white text-sm">{Math.floor((new Date() - new Date(post.created_at)) / (1000 * 60 * 60 * 24))} days</div>
                                </div>
                                {user && canModeratePost(post) && (
                                  <button
                                    onClick={() => {
                                      handleModeratePost(post.id);
                                      setExpandedPost(null);
                                    }}
                                    className="w-full p-3 text-left text-[#C5A52A] hover:bg-zinc-900 text-sm flex items-center gap-2 transition-colors"
                                    data-testid={`moderate-post-${post.id}`}
                                  >
                                    <Shield className="w-4 h-4" />
                                    Hide Post
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    toast.info('Report submitted. Admins will review.');
                                    setExpandedPost(null);
                                  }}
                                  className="w-full p-3 text-left text-red-500 hover:bg-zinc-900 text-sm flex items-center gap-2 transition-colors"
                                >
                                  <Flag className="w-4 h-4" />
                                  Report Post
                                </button>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Post Header */}
                        <div className="flex items-start justify-between mb-4 pr-20">
                          <div className="flex items-center gap-3">
                            <FramedAvatar
                              user={{ profile_image_url: post.user_profile_image, profile_frame: post.user_profile_frame, name: post.user_name }}
                              size={10}
                            />
                            <div>
                              <div className="flex items-center gap-2">
                                <Link 
                                  to={`/profile/${post.user_id}`}
                                  className="font-display text-sm tracking-wider hover:text-[#C5A52A] transition-colors"
                                >
                                  <StyledUsername user={{ accent_color: post.user_accent_color, name_style: post.user_name_style }}>
                                    {post.user_name?.toUpperCase() || 'ANONYMOUS'}
                                  </StyledUsername>
                                </Link>
                              </div>
                              <div className="flex items-center gap-2 text-gray-600">
                                <span className="font-mono text-xs">
                                  {formatDate(post.created_at)}
                                </span>
                                {post.ward && (
                                  <>
                                    <span className="text-gray-700">//</span>
                                    <span className="font-mono text-xs text-[#C5A52A]">
                                      {post.ward}
                                    </span>
                                  </>
                                )}
                                {post.resolved_at && (
                                  <>
                                    <span className="text-gray-700">//</span>
                                    <span className="font-mono text-xs text-green-500 flex items-center gap-1">
                                      <CheckCircle className="w-3 h-3" /> RESOLVED
                                    </span>
                                  </>
                                )}
                                {!post.resolved_at && post.verification_status === 'fixed' && (
                                  <>
                                    <span className="text-gray-700">//</span>
                                    <span className="font-mono text-xs text-emerald-400 flex items-center gap-1" data-testid="verification-badge">
                                      <CheckCircle className="w-3 h-3" /> VERIFIED
                                    </span>
                                  </>
                                )}
                                {post.verification_status === 'still_issue' && (
                                  <>
                                    <span className="text-gray-700">//</span>
                                    <span className="font-mono text-xs text-orange-400 flex items-center gap-1" data-testid="still-issue-badge">
                                      STILL AN ISSUE
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Post Content */}
                        <blockquote className="text-gray-100 leading-loose mb-4 text-lg sm:text-xl tracking-wide">
                          "{post.content}"
                        </blockquote>

                        {/* Post Image */}
                        {post.image_url && (
                          <div className="mb-4 rounded overflow-hidden">
                            <img
                              src={post.image_url.startsWith('http') 
                                ? post.image_url 
                                : getAssetUrl(post.image_url)
                              }
                              alt="Post attachment"
                              className="w-full h-auto"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                        )}

                        {/* Post Footer */}
                        <div className="space-y-4 pt-4 border-t border-zinc-800">
                          <div className="flex items-center justify-between">
                            {/* Left side: User Badges (larger, more prominent) */}
                            <div className="flex items-center gap-2">
                              {post.user_badges && post.user_badges.length > 0 ? (
                                <div className="flex items-center gap-2">
                                  {post.user_badges.slice(0, 3).map((badge, i) => (
                                    <div 
                                      key={badge.id || badge.slug || `badge-${i}`} 
                                      className="relative group"
                                      title={badge.name}
                                    >
                                      {badge.icon && badge.icon.startsWith('http') ? (
                                        <img 
                                          src={badge.icon} 
                                          alt={badge.name} 
                                          className="w-10 h-10 rounded object-cover border border-zinc-700 hover:border-[#C5A52A] transition-colors"
                                        />
                                      ) : (
                                        <div className="w-10 h-10 rounded bg-zinc-800 border border-zinc-700 flex items-center justify-center text-lg">
                                          {badge.icon || '🏅'}
                                        </div>
                                      )}
                                      {/* Badge name tooltip on hover */}
                                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-black border border-zinc-700 rounded text-[10px] font-mono text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                        {badge.name}
                                      </div>
                                    </div>
                                  ))}
                                  {post.user_badges.length > 3 && (
                                    <div className="w-10 h-10 rounded bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                                      <span className="text-xs text-zinc-400 font-mono">+{post.user_badges.length - 3}</span>
                                    </div>
                                  )}
                                </div>
                              ) : post.address ? (
                                <div className="flex items-center gap-2 text-gray-500">
                                  <MapPin className="w-4 h-4" />
                                  <span className="font-mono text-xs">{post.address}</span>
                                </div>
                              ) : null}
                            </div>
                            
                            {/* Right side: Like, Comment & Share buttons */}
                            <div className="flex items-center gap-4">
                              <button
                                onClick={() => handleLike(post.id)}
                                className={`flex items-center gap-2 transition-colors ${
                                  likedPosts[post.id] 
                                    ? 'text-[#C5A52A]' 
                                    : 'text-gray-600 hover:text-[#C5A52A]'
                                }`}
                                data-testid={`like-btn-${post.id}`}
                              >
                                <ThumbsUp className={`w-4 h-4 ${likedPosts[post.id] ? 'fill-current' : ''}`} />
                                <span className="font-mono text-xs">{post.likes || 0}</span>
                              </button>
                              
                              <button
                                onClick={() => toggleComments(post.id)}
                                className={`flex items-center gap-2 transition-colors ${
                                  expandedComments[post.id]
                                    ? 'text-[#C5A52A]'
                                    : 'text-gray-600 hover:text-[#C5A52A]'
                                }`}
                                data-testid={`comments-btn-${post.id}`}
                              >
                                <MessageCircle className="w-4 h-4" />
                                <span className="font-mono text-xs">{post.comment_count || 0}</span>
                              </button>

                              <ShareButton
                                url={`/post/${post.id}`}
                                text={`"${(post.content || '').slice(0, 120)}${(post.content || '').length > 120 ? '...' : ''}" — reported in ${post.ward || 'Regina'} via CYQR.CA`}
                                label="SHARE"
                                size="xs"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Comments Section - Auto-expand if has comments */}
                        {(expandedComments[post.id] || (post.comment_count > 0)) && (
                          <div className="mt-4 pt-4 border-t border-zinc-800">
                            <div className="space-y-3 mb-4">
                              {(comments[post.id] || []).map((comment) => (
                                <div key={comment.id} className="bg-[#0a0a0a] rounded p-4">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-3">
                                      <Link 
                                        to={`/profile/${comment.user_id}`}
                                        className="font-display text-sm text-white hover:text-[#C5A52A] transition-colors"
                                      >
                                        {comment.user_name?.toUpperCase()}
                                      </Link>
                                      <span className="font-mono text-xs text-gray-600">
                                        {formatDate(comment.created_at)}
                                      </span>
                                    </div>
                                    {/* Comment vote buttons */}
                                    <div className="flex items-center gap-3">
                                      <button
                                        onClick={() => handleCommentVote(comment.id, 'up')}
                                        className={`flex items-center gap-1.5 transition-colors ${
                                          commentVotes[comment.id] === 'up' 
                                            ? 'text-green-500' 
                                            : 'text-gray-600 hover:text-green-500'
                                        }`}
                                      >
                                        <ThumbsUp className="w-4 h-4" />
                                        <span className="font-mono text-xs">{comment.upvotes || 0}</span>
                                      </button>
                                      <button
                                        onClick={() => handleCommentVote(comment.id, 'down')}
                                        className={`flex items-center gap-1.5 transition-colors ${
                                          commentVotes[comment.id] === 'down' 
                                            ? 'text-red-500' 
                                            : 'text-gray-600 hover:text-red-500'
                                        }`}
                                      >
                                        <ThumbsDown className="w-4 h-4" />
                                        <span className="font-mono text-xs">{comment.downvotes || 0}</span>
                                      </button>
                                    </div>
                                  </div>
                                  <p className="text-gray-300 text-base leading-relaxed">{comment.content}</p>
                                </div>
                              ))}
                              {(comments[post.id] || []).length === 0 && (
                                <p className="text-gray-600 font-mono text-xs text-center py-2">
                                  {post.comment_count > 0 ? 'Loading comments...' : 'No comments yet'}
                                </p>
                              )}
                            </div>
                            
                            {user ? (
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={newComment[post.id] || ''}
                                  onChange={(e) => setNewComment({ ...newComment, [post.id]: e.target.value })}
                                  placeholder="Add a comment..."
                                  className="flex-1 px-3 py-2 bg-black border border-zinc-800 text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                                  onKeyPress={(e) => e.key === 'Enter' && handleAddComment(post.id)}
                                />
                                <button
                                  onClick={() => handleAddComment(post.id)}
                                  className="bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-display text-xs px-4 tracking-wider transition-colors"
                                >
                                  POST
                                </button>
                              </div>
                            ) : (
                              <p className="text-gray-600 font-mono text-xs text-center">
                                <Link to="/login" className="text-[#C5A52A] hover:underline">Log in</Link> to comment
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </article>

                    {/* Feed ads - 3 side-by-side every 5 posts, rotating through all active ads */}
                    {(index + 1) % 5 === 0 && feedAds.length > 0 && (() => {
                      const adGroupIndex = Math.floor(index / 5);
                      const startIdx = (adGroupIndex * 3) % feedAds.length;
                      const adsToShow = feedAds.length >= 3
                        ? [0, 1, 2].map(i => feedAds[(startIdx + i) % feedAds.length])
                        : feedAds;
                      return (
                      <div className="mb-6">
                        <div className="text-center py-1 mb-2">
                          <span className="font-mono text-[9px] text-gray-600 tracking-widest">SPONSORED</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {adsToShow.map((ad, adIndex) => (
                            <a 
                              key={ad.id || adIndex}
                              href={ad.link || '#'} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="bg-zinc-900 border border-zinc-800 rounded overflow-hidden hover:border-[#C5A52A]/30 transition-all block"
                            >
                              {ad.image_url && (
                                <img 
                                  src={ad.image_url} 
                                  alt={ad.title} 
                                  className="w-full h-24 object-cover bg-black" 
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              )}
                              <div className="p-3">
                                <h4 className="font-display text-sm text-white truncate">{ad.title}</h4>
                                <p className="font-mono text-[10px] text-gray-500 truncate">{ad.content}</p>
                              </div>
                            </a>
                          ))}
                        </div>
                      </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pagination */}
      {totalPosts > postsPerPage && (
        <div className="flex justify-center items-center gap-4 py-8 bg-black" data-testid="pagination">
          <button
            onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); window.scrollTo(0, 0); }}
            disabled={currentPage === 1}
            className="font-mono text-xs uppercase tracking-widest px-6 py-2 border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          <span className="font-mono text-xs text-zinc-500">
            Page {currentPage} of {Math.ceil(totalPosts / postsPerPage)}
          </span>
          <button
            onClick={() => { setCurrentPage(p => Math.min(Math.ceil(totalPosts / postsPerPage), p + 1)); window.scrollTo(0, 0); }}
            disabled={currentPage >= Math.ceil(totalPosts / postsPerPage)}
            className="font-mono text-xs uppercase tracking-widest px-6 py-2 border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      )}

      {/* Issue Verification Modal */}
      {verifyingIssue && (
        <IssueVerificationModal
          issue={verifyingIssue}
          onClose={() => setVerifyingIssue(null)}
          onVerified={() => {
            fetchData();
          }}
        />
      )}
    </Layout>
  );
};

export default Home;
